import React, { useEffect, useRef, useState } from 'react'
import { useConnection, useTick } from './ConnectionContext'
import type { ConnectionLevel } from './ConnectionContext'
import { formatAge, formatClock } from './format'

const LEVEL_TEXT: Record<ConnectionLevel, string> = {
  unknown: 'Verbindung wird geprüft',
  ok: 'Verbunden',
  stale: 'Daten veraltet',
  degraded: 'Verbindung gestört',
  offline: 'Keine Verbindung',
}

export interface StatusBarProps {
  /** Zusätzliche Zeilen im Popover (z. B. "Drohne live: ja"). */
  details?: React.ReactNode
  /** Optionaler Zähler (z. B. offline gespeicherte Aktionen): kleines Abzeichen am Punkt, auch am Handy sichtbar. */
  badge?: number
  /** Beschriftung des Zählers für Tooltip/Screenreader, z. B. "2 Aktionen offline gespeichert". */
  badgeLabel?: string
  className?: string
}

/**
 * Kompakte Pille für den Header (Slot `status` der AppShell): Punkt grün/gelb/rot/grau + "vor X s".
 * Am Handy nur der Punkt (CSS). Antippen/Klicken öffnet Details mit "Erneut versuchen".
 */
export function StatusBar({ details, badge, badgeLabel, className }: StatusBarProps) {
  const c = useConnection()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const now = useTick(1000, true)
  const age = c.lastSuccessAt === null ? null : Math.max(0, now - c.lastSuccessAt)

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); btn.current?.focus() } }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  const badgeText = badge && badge > 0 ? (badgeLabel ?? `${badge} ausstehend`) : null
  const label = `${LEVEL_TEXT[c.level]}${c.lastSuccessAt !== null ? ', letzte Antwort ' + formatAge(age) : ''}${badgeText ? '. ' + badgeText : ''}`
  return (
    <div ref={root} className={`ui-status ${className ?? ''}`}>
      <button
        ref={btn}
        type="button"
        className="ui-status-btn"
        data-level={c.level}
        onClick={() => setOpen(o => !o)}
        aria-label={`${label}. Details öffnen`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={label}
      >
        <span className={`ui-status-dot${c.pending > 0 && c.level === 'ok' ? ' is-busy' : ''}`} aria-hidden="true" />
        <span className="ui-status-txt">{c.lastSuccessAt === null ? '–' : formatAge(age)}</span>
        {badgeText && <span className="ui-status-badge" data-testid="status-badge">{badge}</span>}
      </button>
      {open && (
        <div className="ui-status-pop ui-pop animate-fade-in" role="dialog" aria-label="Verbindungsstatus">
          <div className="ui-status-head" data-level={c.level}>
            <span className="ui-status-dot" aria-hidden="true" />
            <strong>{LEVEL_TEXT[c.level]}</strong>
          </div>
          <dl className="ui-status-dl">
            <dt>Letzte Antwort</dt>
            <dd>{c.lastSuccessAt === null ? '–' : `${formatClock(c.lastSuccessAt)} (${formatAge(age)})`}</dd>
            {c.lastError && (
              <>
                <dt>Letzter Fehler</dt>
                <dd>{c.lastError.message} ({formatClock(c.lastError.at)})</dd>
              </>
            )}
            {c.failCount > 0 && (<><dt>Fehlversuche</dt><dd>{c.failCount}</dd></>)}
            {badgeText && (<><dt>Ausstehend</dt><dd>{badgeText}</dd></>)}
            {!c.online && (<><dt>Netzwerk</dt><dd>Gerät ist offline</dd></>)}
            {c.staleMs > 0 && (<><dt>Veraltet nach</dt><dd>{Math.round(c.staleMs / 1000)} s</dd></>)}
          </dl>
          {details}
          <button type="button" className="ui-status-retry" onClick={() => { c.retry(); setOpen(false) }}>
            Erneut versuchen
          </button>
        </div>
      )}
    </div>
  )
}
